import React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'

/** Matches the stacked setup control height (SetupControls). */
const HEIGHT = 40

/**
 * Two (or more) equal-width options with centred text. Pure (no hooks) so it
 * can be walked in tests. Tapping the selected option is a no-op, so callers
 * never issue a redundant change.
 */
export function SetupSegmented<T extends string | boolean>({ options, value, onChange, lines = 1 }: {
  options: ReadonlyArray<{
    value: T; label: string; accessibilityLabel?: string; disabled?: boolean; testID?: string
    /**
     * Two-weight label: `primary` leads at full size and `detail` follows,
     * smaller and dimmer (team Players: "4" then "(2 teams)"). `label` stays
     * the full text for screen readers.
     */
    primary?: string; detail?: string
  }>
  value: T
  onChange: (value: T) => void
  /** Let labels wrap to two lines (team Players: "6 (2 teams)" on phone). */
  lines?: 1 | 2
}) {
  return <View className="flex-row" style={{ gap: 8, width: '100%' }}>
    {options.map((option) => {
      const selected = option.value === value
      return <TouchableOpacity key={String(option.value)}
        onPress={() => { if (!selected) onChange(option.value) }}
        disabled={option.disabled}
        accessibilityLabel={option.accessibilityLabel ?? option.label}
        accessibilityRole="button"
        testID={option.testID}
        accessibilityState={{ selected, disabled: !!option.disabled }}
        className={`flex-1 items-center justify-center rounded-md px-1 border-2 ${selected ? 'bg-blood border-ink' : 'bg-paper-up border-olive'}`}
        style={{ height: HEIGHT, ...(option.disabled ? { opacity: 0.4 } : null) }}
      >
        {option.primary !== undefined
          // Side by side and centred on each other, not nested: nested text
          // sits on a shared baseline, which drops the small detail low.
          // Wraps under the number when the button is narrow (phones).
          ? <View className="flex-row flex-wrap items-center justify-center" style={{ columnGap: 4 }}>
            <Text className={`font-stencil ${selected ? 'text-paper' : 'text-ink'}`} style={{ fontSize: 14 }}>{option.primary}</Text>
            {option.detail ? <Text className={`font-stencil ${selected ? 'text-paper' : 'text-ink'}`} style={{ fontSize: 9, opacity: 0.72 }}>{option.detail}</Text> : null}
          </View>
          : <Text className={`font-stencil ${selected ? 'text-paper' : 'text-ink'}`} style={lines === 2 ? { fontSize: 11, textAlign: 'center' } : { fontSize: 11 }} numberOfLines={lines}>{option.label}</Text>}
      </TouchableOpacity>
    })}
  </View>
}
